import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import events from '../../../lib/events.js';

const stopEvent = e => e.preventDefault();

class EditableTextField extends React.PureComponent {
  static displayName = 'EditableTextField';

  static propTypes = {
    beforeFocus: PropTypes.func,
    dataKey: PropTypes.string.isRequired,
    defaultValue: PropTypes.any,
    disabled: PropTypes.bool,
    didValidate: PropTypes.func,
    editMode: PropTypes.bool.isRequired,
    error: PropTypes.bool,
    format: PropTypes.func,
    formatted: PropTypes.node,
    maxLength: PropTypes.string,
    nullText: PropTypes.node,
    onBlur: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    type: PropTypes.string,
    unit: PropTypes.string,
    validate: PropTypes.func,
    validateMessage: PropTypes.any,
    value: PropTypes.any,
    group: PropTypes.object,
    required: PropTypes.bool,
    visible: PropTypes.bool, // Useful to hide the component but still have lifecycle methods fire
    grayedOut: PropTypes.bool,
    errorMessage: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
    warning: PropTypes.bool,
  };

  static defaultProps = {
    error: false,
    value: '',
    visible: true,
  };

  state = {
    correct: true,
    validate: null,
  };

  componentWillMount() {
    if (this.props.defaultValue) {
      this.setState({
        value: this.props.defaultValue,
      });
    }
  }

  componentDidMount() {
    if (this.props.dataKey && this.refs.input) {
      let listener = events.subscribe('focus|' + this.props.dataKey, () => {
        this.refs.input.focus();
        this.refs.input.scrollIntoView(false);
      });

      if (this.refs.input && this.props.type === 'number') {
        this.refs.input.addEventListener('mousewheel', stopEvent);
      }
      this.listener = listener;
    }

    if (this.props.group) {
      this.props.group.addComponent(this);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.value !== this.props.value && !this.state.correct) {
      this.validate(nextProps.value);
    }

    if (this.state.highlighted && nextProps.editMode !== this.props.editMode) {
      this.setState({
        highlighted: false,
      });
    }
  }

  componentWillUnmount() {
    if (this.listener) {
      this.listener.remove();
      if (this.refs.input) {
        this.refs.input.removeEventListener('mousewheel', stopEvent);
      }
    }

    if (this.props.group) {
      this.props.group.removeComponent(this);
    }
  }

  listener = null;

  validate = (val = this.props.value) => {
    if (!this.props.visible) {
      return true;
    }

    if (this.props.required && !val && val !== 0) {
      this.setState({
        correct: false,
      });
      return false;
    }

    if (!this.state.correct && this.props.required && val && !this.props.validate) {
      this.setState({
        correct: true,
      });
      return true;
    }

    if (!this.props.validate) {
      this.setState({
        correct: true,
      });
      return true;
    }

    const handleResult = (validateResult) => {
      if (validateResult === false) {
        this.setState({
          correct: false,
        });

        if ((val || val === 0) && this.props.validateMessage && !this.props.errorMessage) {
          events.publish('error.user', {
            message: this.props.validateMessage,
            key: this.props.dataKey,
          });
        }
        return false;
      }

      this.setState({correct: true});
      events.publish('cancel.error', {key: this.props.dataKey});
      return true;
    };

    let validateResult = this.props.validate(val);

    if (validateResult instanceof Promise) {
      validateResult.then(handleResult);
      return validateResult;
    } else {
      return handleResult(validateResult);
    }
  };

  focus = () => {
    if (this.refs.input) {
      if (this.props.beforeFocus) {
        this.props.beforeFocus();
      }
      this.refs.input.focus();
      this.refs.input.scrollIntoView(false);
    }
  };

  highlight = () => {
    this.setState({highlighted: true});
  };

  handleChange = (event) => {
    let val = event.target.value;

    if (this.props.format) {
      val = this.props.format(val);
    }
    this.props.onChange({
      key: this.props.dataKey,
      value: val,
    });
    let correct = this.state.correct;

    if (!correct) {
      this.validate(val);
    }
  };

  focusHandler = () => {
    if (this.state.highlighted) {
      this.setState({highlighted: false});
    }
  };

  render() {
    let content;
    let error = null;

    if (!this.props.visible) {
      return null;
    }

    if (this.props.editMode) {
      content =
        <input
          id={this.props.dataKey}
          type={this.props.type ? this.props.type : 'text'}
          ref='input'
          maxLength={this.props.maxLength ? this.props.maxLength : null}
          className={classNames('form-control', {
            'error': this.props.error || !this.state.correct,
            'form-control--highlighted': this.state.highlighted,
            'warning': this.props.warning,
            'bold-placeholder': this.props.required})}
          onBlur={this.props.onBlur ? this.props.onBlur : null}
          onFocus={this.focusHandler}
          value={this.props.value !== undefined && this.props.value !== null ? this.props.value.toString() : ''}
          placeholder={this.props.placeholder}
          disabled={this.props.disabled}
          min={this.props.min}
          max={this.props.max}
          onChange={this.handleChange} />
      ;
    } else if (this.props.formatted) {
      content =
        <div className='form-control-static'>
          {this.props.formatted}
        </div>
      ;
    } else {
      content =
        <div className={classNames('form-control-static',{
          'form-control-static--grayedout': this.props.grayedOut,
        })} style={{overflowWrap: 'break-word'}}>
          {!this.props.value && this.props.nullText ? this.props.nullText : this.props.value}
          {this.props.unit && this.props.value ? ' ' + this.props.unit : null}
        </div>
      ;
    }

    if (this.props.value && this.props.errorMessage) {
      error = <div className="form-control-error">
        <i className='icon bl bl-attention'/>
        <span>{this.props.errorMessage}</span>
      </div>
      ;
    }

    return (
      <div className='editable-textfield'>
        {content}
        {error}
      </div>
    );
  }
}

export default EditableTextField;
